import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasPermissionPage } from '@hooks/hasPermissionPage';
import { currentUserSelector } from '@selectors/user';
import { isLoggedInSelector } from '@selectors/app/registeredRouteSelector';
import { AuthPaths, Paths } from '@constants/paths';
import { FeaturePermission, PagePermission, PermissionActions } from '@enums';
import { hasPermissionFeature } from '@hooks/hasPermissionFeature';
import { registerRoute } from '@actions/appActions';

interface Props {
  permissionPages: PagePermission[];
  permissionActions: PermissionActions[];
  permissionFeatures?: FeaturePermission[] | [];
  actionFeatures?: PermissionActions[] | [];
  children: JSX.Element;
}

export const PrivatePermissionRoute = ({
  children,
  permissionPages,
  permissionActions,
  permissionFeatures = [],
  actionFeatures = [],
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isLoggedIn = useSelector(isLoggedInSelector);
  const currentUser = useSelector(currentUserSelector);

  const hasPagePermission = hasPermissionPage(permissionPages, permissionActions);
  const hasFeaturePermission = hasPermissionFeature(
    permissionPages[0],
    permissionFeatures,
    actionFeatures
  );

  useEffect(() => {
    if (!isLoggedIn) {
      dispatch(registerRoute(pathname));
      navigate(`${Paths.AUTH}/${AuthPaths.TEST_LOGIN}`);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!hasFeaturePermission && isLoggedIn) {
      dispatch(registerRoute(pathname));
      navigate(Paths.NOT_FOUND);
    }
  }, [hasFeaturePermission, isLoggedIn]);

  useEffect(() => {
    if (!hasPagePermission && isLoggedIn) {
      navigate(Paths.NOT_FOUND);
    }
  }, [currentUser, isLoggedIn]);

  return <>{isLoggedIn && hasPagePermission && hasFeaturePermission ? children : ''}</>;
};
